import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import { PaymentTypes } from 'appConstants';
import { formatPrice } from 'utils/formating';
import {
  RenewSubscriptionFormValues,
  SubscriptionsParser,
} from './models';
import { PopoutMenu } from 'components/popupsAndPanels/PopoutMenu';
import { PopoutMenuFooter } from 'components/popupsAndPanels/PopoutMenuFooter';
import { PopoutMenuHeader } from 'components/popupsAndPanels/PopoutMenuHeader';
import FormGeneralError from 'components/formComponents/FormGeneralError';
import FormTextField from 'components/formComponents/FormTextField';
import FormStripeCardField from 'components/formComponents/FormStripeCardField';
import { useFetchRenewalPricing } from 'hooks/useFetchRenewalPricing';
import { useFetchOptions } from 'hooks/useFetchOptions';
import { REVERSE_CHARGE_NOTE, vatDisplay } from 'subscriptionOptions/vatDisplay';
import { PaymentVatRow } from 'components/paymentVatRow/PaymentVatRow';
import { GridSelection, OptionsType } from 'subscriptionOptions/types';
import { displayRows, PromoCodeBySubject, RenewalPromoCode } from 'subscriptionOptions/renewalPromoCodes';
import { AppliedPromoCodes } from 'components/appliedPromoCodes/AppliedPromoCodes';
import { usePayNowPromoCodes } from 'hooks/usePayNowPromoCodes';
import { payNowHeldCodes } from 'subscriptionOptions/payNowPromoCodes';
import { RenewalSelectedPackage } from 'subscriptionOptions/renewalPricing';
import { renewalPackagePromos } from 'subscriptionOptions/paymentPromos';
import { CardPaymentElements } from 'cardPayment/CardPaymentElements';
import { useCardPayment } from 'cardPayment/useCardPayment';
import { CardPaymentReceipt } from 'cardPayment/types';
import { CardPaymentProcessing } from 'components/cardPayment/CardPaymentProcessing';
import { CardPaymentResult } from 'components/cardPayment/CardPaymentResult';

type Props = {
  panelOpen: boolean;
  togglePanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  menuTitle: string;
  id: number[];
  setUpdateList: React.Dispatch<React.SetStateAction<boolean>>;
  packageIds: number[] | null;
  setSubsToPay: any;
  setPkgsToPay: any;
  setPaymentMethod: Dispatch<SetStateAction<string | null>>;
  /** subjectsId -> packagesId, the panel's own picks. */
  selection: GridSelection;
  /** The renewal codes the panel was priced under; the modal starts from these. */
  promoCodeBySubject: PromoCodeBySubject;
  /** The account's renewal codes for these subjects, for their expiry dates. */
  promoCodes: RenewalPromoCode[];
};

const InnerRenewSubscriptions: React.FC<Props> = ({
  panelOpen,
  togglePanelOpen,
  menuTitle,
  id,
  setUpdateList,
  packageIds,
  setSubsToPay,
  setPkgsToPay,
  setPaymentMethod,
  selection,
  promoCodeBySubject,
  promoCodes,
}) => {
  const { pay, stage, isPaying } = useCardPayment('school');
  const [receipt, setReceipt] = useState<CardPaymentReceipt | null>(null);

  const [error, setError] = useState<string>('');

  const subjectIds = id;

  const formikRef: any = useRef<FormikProps<any>>(null);

  // Changes here are for this payment only; the renewal codes are never written.
  const { slots: payNowSlots, dropped, applyCode, removeCode } = usePayNowPromoCodes(selection, promoCodeBySubject);

  // The panel chose the packages; pricing says what they cost.
  const {
    priceInfo,
    selectedPackages,
    pricedPackages,
    loading: pricingLoading,
    error: pricingError,
    reload: reloadPricing,
  } = useFetchRenewalPricing(selection, payNowSlots);

  const [promoCode, setPromoCode] = useState({ value: '', error: '' });
  const [applyingPromoCode, setApplyingPromoCode] = useState<boolean>(false);

  const applyPromoCodeToBasket = async () => {
    const code = promoCode.value;
    if (!code || applyingPromoCode) return;

    setApplyingPromoCode(true);
    const message = await applyCode(code);
    setApplyingPromoCode(false);

    setPromoCode({ value: message ? code : '', error: message });
  };

  const appliedPromoCodes = (priceInfo && priceInfo.appliedPromoCodes) || [];
  const promoCodeRows = displayRows(payNowHeldCodes(payNowSlots, promoCodes), pricedPackages);

  // Prefills the receipt address; the packages themselves were already chosen in the panel.
  const { accountContext, loading: optionsLoading } = useFetchOptions(OptionsType.RENEW, true, { subjectsIDs: subjectIds });
  const vatMode = vatDisplay(accountContext, null);
  const financeEmail = (accountContext && accountContext.financeEmail) || '';

  useEffect(() => {
    return () => {
      setPaymentMethod(null);
    };
  }, []);      

  useEffect(() => {
    return () => {
      setSubsToPay(null);
      setPkgsToPay(null);
    };
  }, []);    

  const handleMenuClose = () => {
    if (isPaying) return;

    if (receipt) {
      setUpdateList(true);
    }

    togglePanelOpen(false);
  };

  const payByCard = (
    values: RenewSubscriptionFormValues,
    setSubmitting: (status: boolean) => void
  ) => {
    if (!priceInfo) return;

    const total = priceInfo.total;
    const items = selectedPackages.map((pkg: RenewalSelectedPackage) => ({
      title: pkg.subjectName,
      detail: `${pkg.ageGroup} - ${pkg.subscriptionLength}`,
    }));

    setError('');

    pay(
      {
        orderType: 'RENEW',
        packagePromos: renewalPackagePromos(priceInfo.packageId, priceInfo.promoCodeBySubject),
        expectedGrossAmount: total,
        poNumber: values.poNumber,
        financeEmail: values.FinanceEmail,
      },
      values.cardholderName,
      (outcome) => {
        if (outcome.kind === 'failed') {
          setSubmitting(false);
          setError(outcome.message);

          if (outcome.action === 'reprice') {
            reloadPricing();
          }

          if (outcome.action === 'subscriptions') {
            setUpdateList(true);
          }
          return;
        }

        setReceipt({ outcome: outcome.kind, items, total });
      }
    );
  };

  const handleFormSubmission = (
    values: RenewSubscriptionFormValues,
    { setFieldError, setSubmitting }: FormikHelpers<RenewSubscriptionFormValues>
  ) => {
    // Never submit a payment before the basket has priced, or against a price its codes no longer match.
    if (!priceInfo || pricingLoading || pricingError) {
      setSubmitting(false);
      setError('Sorry, something went wrong. Please try again.');
      return;
    }

    if (values.paymentType === PaymentTypes.PAY_BY_CARD) {
      if (!values.cardholderName) {
        setFieldError('cardholderName', 'Please provide the cardholder name');
        setSubmitting(false);
        return;
      }
      if (!values.paymentType) {
        setFieldError('paymentType', 'Please provide a type of payment');
        setSubmitting(false);
        return;
      }
      payByCard(values, setSubmitting);
    }
  };
  
  return (
    <Formik
      initialValues={SubscriptionsParser.createEmptyRenewPackages(
        subjectIds,
        financeEmail
      )}
      onSubmit={(values, formikHelpers) =>
        handleFormSubmission(values, formikHelpers)
      }
      validationSchema={Yup.object({
        FinanceEmail: Yup.string()
          .email('Please enter a valid email')
          .required('A finance email is required')
          .nullable(),
      })}
      enableReinitialize={true}
      innerRef={formikRef}
    >
      {({ values, submitForm, isSubmitting }) => {
        return (
          <Form>
            <PopoutMenu
              isPopup={true}
              isOpen={panelOpen}
              handleClose={handleMenuClose}
              className="SubscriptionsPopoutMenu"
              position="right"
              loading={pricingLoading || optionsLoading}
            >
              <PopoutMenuHeader title={menuTitle} handleClose={handleMenuClose} />
              <CardPaymentProcessing stage={stage} />
              <div className="PopoutMenuContent">
                {receipt && <CardPaymentResult receipt={receipt} />}

                {!receipt && <FormGeneralError message={error || pricingError} />}

                {!receipt && priceInfo && (
                  <>
                    {selectedPackages && selectedPackages.length > 0 &&
                      <div className="SelectedPackages">
                        <h4>Selected Subscriptions</h4>
                        {selectedPackages.map((pkg: RenewalSelectedPackage) => {
                          return (
                            <div className="SPRow" key={pkg.packageId}>
                              <div className="SPInfo"><span className="SubName">{pkg.subjectName}</span> <span className="SubAG">{pkg.ageGroup} - {pkg.subscriptionLength}</span></div>
                              <div className="SPCost">£{pkg.discountedPrice.toFixed(2)}</div>
                            </div>
                          );
                        })}
                      </div>
                    }

                    <div className="DiscoundContainer">
                      <input
                        className="Discount"
                        type="text"
                        onChange={(e) =>
                          setPromoCode((prevState) => ({
                            ...prevState,
                            value: e.target.value,
                          }))
                        }
                        value={promoCode.value}
                        placeholder="Enter promo code (optional)"
                        disabled={isPaying}
                      />

                      <button
                        className="Btn BtnOrange"
                        type="button"
                        disabled={promoCode.value && !applyingPromoCode && !isPaying ? false : true}
                        onClick={applyPromoCodeToBasket}
                      >
                        Apply
                      </button>

                      <FormGeneralError message={promoCode.error} />
                    </div>

                    <AppliedPromoCodes rows={promoCodeRows} onRemove={removeCode} dropped={dropped} />
                    <p>Enter email address to send card receipt to:</p>
                    <FormTextField
                      field={'FinanceEmail'}
                      placeholder={'Email address'}
                      required={true}
                      value={values.FinanceEmail}
                      disabled={isPaying}
                    />
                    <div className="LineSpacer4"></div>
                    {values.paymentType === PaymentTypes.PAY_BY_CARD && (
                      <>
                        <h4>Payment details </h4>
                        <div className="CardPaymetFields">
                          <FormTextField
                            field={'cardholderName'}
                            label={"Cardholder's name"}
                            // labelPosition={'left'}
                            required={true}
                            disabled={isPaying}
                          />

                          <FormStripeCardField
                            field={'card'}
                            label={'Card '}
                            required={true}
                            disabled={isPaying}
                            // labelPosition={'left'}
                          />
                        </div>
                      </>
                    )}
                    <div
                      className="TotalDetails"
                      style={{ width: '100%', paddingTop: '20px' }}
                    >
                      {appliedPromoCodes.length > 0 && priceInfo.promoCodeApplied &&
                        <div className="DetailsItem">
                          <p>{appliedPromoCodes.length > 1 ? 'Applied Promo Codes' : 'Applied Promo Code'}</p>
                          <span><strong>{appliedPromoCodes.join(', ')}</strong></span>
                        </div> 
                      } 

                      <div className="DetailsItem">
                        <p>Price</p>
                        <span>£ {formatPrice(priceInfo.price)}</span>
                      </div>

                      {priceInfo.discount > 0 && (
                        <div className="DetailsItem">
                          <p>Discount</p>
                          <span>
                            -£
                            {formatPrice(priceInfo.discount)}
                          </span>
                        </div>
                      )}

                      <PaymentVatRow mode={vatMode} vatRate={priceInfo.vatRate} vat={priceInfo.vat} />

                      <div className="DetailsItem">
                        <p>Total price</p>
                        <span>£ {formatPrice(priceInfo.total)}</span>
                      </div>
                    </div>

                    {vatMode === 'reverseCharge' && <p className="VATInfoTxt">{REVERSE_CHARGE_NOTE}</p>}
                  </>
                )}
              </div>

              {receipt ? (
                <PopoutMenuFooter primaryButtonText="Close" handlePrimaryButtonClick={handleMenuClose} />
              ) : (
                <PopoutMenuFooter
                  primaryButtonText="Pay now"
                  secondaryButtonText="Cancel"
                  handlePrimaryButtonClick={submitForm}
                  handleSecondaryButtonClick={handleMenuClose}
                  isSubmitting={isSubmitting || isPaying}
                />
              )}
            </PopoutMenu>
          </Form>
        );
      }}
    </Formik>
  );
};

export const RenewSubscriptionsForm: React.FC<Props> = (props) => {
  useEffect(() => {
    return () => {
      document.body.style.overflowY = '';
    };
  }, []);	 

  return (
    <CardPaymentElements>
      <InnerRenewSubscriptions {...props} />
    </CardPaymentElements>
  );
};
